import React, { useState, useMemo } from 'react';
import { FaChevronDown } from 'react-icons/fa';

interface MessageContentProps {
  content: string;
}

interface Section {
  heading: string;
  content: string;
}

const MessageContent: React.FC<MessageContentProps> = ({ content }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const isHTMLContent = (text: string): boolean => {
    if (!text || typeof text !== 'string') return false;
    const trimmed = text.trim();
    return trimmed.startsWith('<') && (trimmed.includes('</') || trimmed.includes('/>') || trimmed.includes('<div') || trimmed.includes('<h') || trimmed.includes('<p') || trimmed.includes('<span'));
  };

  const formatMarkdownText = (text: string): React.ReactNode => {
    if (!text) return '';

    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const boldText = part.substring(2, part.length - 2);
        return <strong key={index}>{boldText}</strong>;
      }
      return <React.Fragment key={index}>{part}</React.Fragment>;
    });
  };


  const dedentText = (text: string): string => {
    if (!text) return '';
    
    const lines = text.split('\n');
    if (lines.length <= 1) return text;
    
    const nonEmptyLines = lines.filter(line => line.trim().length > 0);
    if (nonEmptyLines.length === 0) return text;

    const minIndent = nonEmptyLines.reduce((min, line) => {
      const match = line.match(/^\s*/);
      const indent = match ? match[0].length : 0;
      return (indent < min) ? indent : min;
    }, Infinity);
    
    if (minIndent && minIndent < Infinity) {
      return lines.map(line => {
        if (line.trim().length === 0) return line;
        return line.substring(minIndent);
      }).join('\n');
    }
    
    return text;
  };


  const { sections, preHeadingContent } = useMemo(() => {
    if (!content || typeof content !== 'string') {
      return { sections: [], preHeadingContent: '' };
    }

    try {

      const normalizedContent = content.replace(/\\n/g, '\n');

      const lines = normalizedContent.split('\n');
      const parsedSections: Section[] = [];
      
      let currentHeading: string | null = null;
      let currentContent: string[] = [];
      let preHeadingLines: string[] = [];
      let foundFirstHeading = false;
      

      lines.forEach(line => {
        const trimmedLine = line.trim();
        const isHeading = trimmedLine.startsWith('## ') || trimmedLine.startsWith('### ');
        
        if (isHeading) {
          foundFirstHeading = true;

          if (currentHeading !== null) {
            parsedSections.push({
              heading: currentHeading,
              content: dedentText(currentContent.join('\n'))
            });
          }
          
          currentHeading = trimmedLine.replace(/^(##|###)\s+/, '');
          currentContent = [];
        } else if (currentHeading !== null) {
          currentContent.push(line);
        } else if (!foundFirstHeading) {
          preHeadingLines.push(line);
        }
      });

      if (currentHeading !== null) {
        parsedSections.push({
          heading: currentHeading,
          content: dedentText(currentContent.join('\n'))
        });
      }
      
      return { 
        sections: parsedSections,
        preHeadingContent: dedentText(preHeadingLines.join('\n'))
      };
    } catch (error) {
      console.error('Error parsing content:', error);
      return { sections: [], preHeadingContent: content };
    }
  }, [content]);

  if (isHTMLContent(content)) {
    return (
      <div 
        className="mb-2 text-body text-primary-text"
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  }

  if (!sections.length) {
    return (
      <div className="whitespace-pre-wrap mb-2 text-body text-primary-text p-4">
        {formatMarkdownText(content)}
      </div>
    );
  }

  return (
    <div className="mb-2 bg-white rounded-lg overflow-hidden">

      {preHeadingContent && (
        <div className="px-4 py-3">
          <div className="whitespace-pre-wrap text-body text-primary-text leading-relaxed">
            {formatMarkdownText(preHeadingContent)}
          </div>
        </div>
      )}
      
      <div className="px-4 py-3 border-b border-gray-100">

        <div className="text-body text-primary-text leading-relaxed whitespace-pre-wrap mb-2">
          <div className="mb-2 text-body text-primary-text">
            {formatMarkdownText(sections[0].heading)}
          </div>
          <div className="whitespace-pre-wrap text-body text-primary-text leading-relaxed">
            {formatMarkdownText(sections[0].content)}
          </div>
        </div>

        {sections.length > 1 && (
          <div className="flex justify-end">
            <button type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="custom-border text-primary text-xs flex items-center px-2 py-1 rounded hover:bg-[#e6e6e6] transition-colors"
            >
              {isExpanded ? "Show less" : "Show more"}
              <FaChevronDown
                className={`ml-1 transition-transform ${isExpanded ? "rotate-180" : ""}`}
              />
            </button>
          </div>
        )}
      </div>
      
      {sections.length > 1 && (
        <div className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-full' : 'max-h-0'}`}
          aria-hidden={!isExpanded}
        >
          <div className="px-4 py-3 space-y-4">
            {sections.slice(1).map((section, index) => (
              <div key={index} className="text-gray-700">
                <div className="font-medium mb-2 text-body text-primary-text">
                  {formatMarkdownText(section.heading)}
                </div>
                <div className="whitespace-pre-wrap text-body text-primary-text leading-relaxed">
                  {formatMarkdownText(section.content)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageContent;